import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Badge,
  Spinner,
} from '@heroui/react';
import { MoreVertical } from 'lucide-react';

export interface TableColumn {
  name: string;
  uid: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface TableAction {
  key: string;
  label: string;
  icon?: React.ReactNode;
  color?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  variant?: 'solid' | 'bordered' | 'light' | 'flat' | 'faded' | 'shadow' | 'ghost';
  onClick: (item: any) => void;
}

export interface TableActionColumn {
  type: 'actions';
  actions: TableAction[];
  showQuickActions?: boolean;
  quickActions?: TableAction[];
}

export interface BaseTableProps<T = any> {
  data: T[];
  columns: TableColumn[];
  isLoading?: boolean;
  loadingMessage?: string;
  error?: string | null;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;
  emptyAction?: {
    label: string;
    onClick: () => void;
  };
  onSort?: (column: string, direction: 'ascending' | 'descending') => void;
  renderCell?: (item: T, columnKey: string) => React.ReactNode;
  classNames?: {
    wrapper?: string;
    table?: string;
    thead?: string;
    tbody?: string;
    tr?: string;
    th?: string;
    td?: string;
  };
  stickyHeader?: boolean;
  hoverable?: boolean;
  striped?: boolean;
  compact?: boolean;
  idField?: keyof T;
}

const BaseTableCell = ({
  icon,
  text,
  description,
  maxLines = 1,
  maxWidth = 'max-w-xs',
}: {
  icon?: React.ReactNode;
  text: string;
  description?: string;
  maxLines?: number;
  maxWidth?: string;
} & React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className="flex items-center gap-4">
      {icon}
      <div className="flex flex-col">
        <span className="text-sm text-gray-700 dark:text-gray-300">{text}</span>
        {description && (
          <div
            className={`text-sm text-gray-500 dark:text-gray-400 ${maxWidth} ${
              maxLines > 1 ? `line-clamp-${maxLines}` : 'line-clamp-1'
            }`}
          >
            {description}
          </div>
        )}
      </div>
    </div>
  );
};

const BaseTable = <T extends Record<string, any>>({
  data,
  columns,
  isLoading = false,
  loadingMessage = 'Loading data...',
  error = null,
  emptyMessage = 'No data found',
  emptyIcon,
  emptyAction,
  onSort,
  renderCell,
  classNames = {},
  stickyHeader = true,
  hoverable = true,
  striped = false,
  compact = false,
  idField = 'id' as keyof T,
}: BaseTableProps<T>) => {
  const [sortDescriptor, setSortDescriptor] = useState<
    | {
        column: string;
        direction: 'ascending' | 'descending';
      }
    | undefined
  >(undefined);

  const defaultClassNames = {
    thead: 'bg-gray-50 dark:bg-gray-700',
    tbody: striped ? 'divide-y divide-gray-200 dark:divide-gray-700' : '',
    tr: hoverable ? 'hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors' : '',
    th: 'text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-6 py-3',
    td: compact ? 'px-4 py-2 text-sm' : 'px-6 py-4 text-sm',
  };

  const mergedClassNames = { ...defaultClassNames, ...classNames };

  const sortedData = useMemo(() => {
    console.log('Sorting data with descriptor:', sortDescriptor);
    if (!data || data.length === 0) return [];
    if (!sortDescriptor || !sortDescriptor.column) {
      console.log('No sort column, returning original data');
      return data;
    }

    const sorted = [...data].sort((a, b) => {
      const { column, direction } = sortDescriptor;
      let aValue = a[column];
      let bValue = b[column];

      console.log(`Comparing ${aValue} vs ${bValue} for column ${column}`);

      // Handle null/undefined values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      if (aValue < bValue) {
        return direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });

    console.log('Sorted data:', sorted);
    return sorted;
  }, [data, sortDescriptor]);

  const handleSortChange = (descriptor: any) => {
    console.log('Sort descriptor received:', descriptor);
    console.log('Type of descriptor:', typeof descriptor);
    console.log('Is array:', Array.isArray(descriptor));

    // HeroUI might pass the descriptor directly as { column, direction }
    if (descriptor && typeof descriptor === 'object' && 'column' in descriptor) {
      console.log('Using descriptor as-is');
      setSortDescriptor(descriptor);

      if (onSort) {
        onSort(descriptor.column, descriptor.direction);
      }
      return;
    }

    // If it's undefined or null, clear sorting
    if (!descriptor) {
      console.log('Clearing sort descriptor');
      setSortDescriptor(undefined);
      return;
    }

    console.log('Unknown descriptor format, ignoring');
  };

  const renderDefaultCell = (item: T, columnKey: string) => {
    const value = item[columnKey];

    if (value === null || value === undefined) {
      return <span className="text-gray-400">—</span>;
    }

    if (typeof value === 'boolean') {
      return (
        <Badge color={value ? 'success' : 'warning'} variant="flat" size="sm">
          {value ? 'Yes' : 'No'}
        </Badge>
      );
    }

    if (typeof value === 'object' && value !== null) {
      return <span className="text-gray-500">Object</span>;
    }

    return <span className="text-gray-900 dark:text-gray-100">{String(value)}</span>;
  };

  const renderActionCell = (item: T, actionColumn: TableActionColumn) => {
    const { actions, showQuickActions = true, quickActions = [] } = actionColumn;
    const displayActions = showQuickActions ? quickActions.slice(0, 2) : [];
    const dropdownActions = showQuickActions ? actions.slice(2) : actions;

    return (
      <div className="flex items-center space-x-2">
        {/* Quick action buttons */}
        {displayActions.map((action) => (
          <Button
            key={action.key}
            isIconOnly
            size="sm"
            variant="light"
            color={action.color}
            onPress={() => action.onClick(item)}
            aria-label={action.label}
          >
            {action.icon}
          </Button>
        ))}

        {/* Dropdown for additional actions */}
        {dropdownActions.length > 0 && (
          <Dropdown>
            <DropdownTrigger>
              <Button isIconOnly size="sm" variant="light" aria-label="More actions">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownTrigger>
            <DropdownMenu aria-label="Table actions">
              {dropdownActions.map((action) => (
                <DropdownItem
                  key={action.key}
                  startContent={action.icon}
                  color={action.color}
                  onPress={() => action.onClick(item)}
                >
                  {action.label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        )}
      </div>
    );
  };

  const renderTableCell = (item: T, column: TableColumn | TableActionColumn) => {
    if ('type' in column && column.type === 'actions') {
      return renderActionCell(item, column);
    }

    const columnKey = 'uid' in column ? column.uid : '';
    const cellContent = renderCell ? renderCell(item, columnKey) : renderDefaultCell(item, columnKey);

    return <div className={`text-gray-900 dark:text-gray-100 `}>{cellContent}</div>;
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="text-red-500 text-center">
          <div className="w-12 h-12 mx-auto mb-4 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
            <span className="text-2xl">⚠️</span>
          </div>
          <h3 className="text-lg font-semibold mb-2">Error Loading Data</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <Table
        aria-label="Data Table"
        isHeaderSticky={stickyHeader}
        sortDescriptor={sortDescriptor}
        onSortChange={handleSortChange}
        classNames={mergedClassNames}
      >
        <TableHeader columns={columns}>
          {(column) => {
            const columnKey = 'uid' in column ? column.uid : '';
            const columnName = 'name' in column ? column.name : '';

            return (
              <TableColumn key={columnKey} allowsSorting={column.sortable}>
                {columnName}
              </TableColumn>
            );
          }}
        </TableHeader>
        <TableBody
          items={sortedData}
          isLoading={isLoading}
          loadingContent={
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">{loadingMessage}</span>
            </div>
          }
          emptyContent={
            <div className="flex flex-col items-center justify-center py-12">
              {emptyIcon || (
                <div className="w-12 h-12 text-gray-400 mb-4">
                  <svg
                    className="w-full h-full"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
              )}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{emptyMessage}</h3>
              {emptyAction && (
                <Button color="primary" onPress={emptyAction.onClick} className="mt-4">
                  {emptyAction.label}
                </Button>
              )}
            </div>
          }
        >
          {(item) => (
            <TableRow key={String(item[idField])}>
              {(columnKey) => {
                const column = columns.find((col) => ('uid' in col ? col.uid : '') === columnKey);
                if (!column)
                  return (
                    <TableCell>
                      <span className="text-gray-400">—</span>
                    </TableCell>
                  );
                return <TableCell>{renderTableCell(item, column)}</TableCell>;
              }}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export { BaseTableCell };
export default BaseTable;
